import './invite-modal.scss';
import {
  CircularProgress,
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Dialog,
  Box,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {ChangeEvent, useContext, useEffect, useState} from 'react';
import axios, {CancelTokenSource, CancelTokenStatic} from 'axios';
import {Invite} from '../../../interfaces/invite';
import {InviteIdentificationType} from '../../../enums/invite-identification-type';
import {InvitesRepository} from '../../../repositories/invites';
import {CancelRequestError} from '../../../enums/cancel-request-error';
import {SnackBarContextType} from '../../../contexts/snack-bar/types';
import {SnackBarContext} from '../../../contexts/snack-bar/context';
import {RelationshipType} from '../../../backend/enums/relationship-type';
import {AuthContextType} from '../../../contexts/auth/types';
import {AuthContext} from '../../../contexts/auth/context';
import {ProcessStatus} from '../../../backend/enums/process-status';
import {InviteState} from '../../../backend/enums/invite-state';
import {relationshipTypes} from '../../../utils/data/relationship-types';
import {NotificationModal} from '../notification-modal';
import {PatchResolveInvite} from '../../../backend/requests/patch-resolve-invite';
import {RelationshipInviteData} from '../../../backend/interfaces/relationship-invite-data';
import {Invite as BackendInvite} from '../../../backend/interfaces/invite';
import {InviteType} from '../../../backend/enums/invite-type';
import {AffiliationInviteData} from '../../../backend/interfaces/affiliation-invite-data';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LoadingButton from '@mui/lab/LoadingButton';
import WarningIcon from '@mui/icons-material/Warning';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';

/**
 * Invite modal for relationship and affiliation invites.
 */
export function InviteModal(props: {
  identificationType: InviteIdentificationType;
  identificationValue: string;
  opened: boolean;
  handleClose: () => void;
}) {
  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const authContext: AuthContextType = useContext(AuthContext);

  // Hold state
  const [inviteDoneWarning, setInviteDoneWarning] = useState<string>('');
  const [isInviteLoading, setIsInviteLoading] = useState<boolean>(true);
  const [isAcceptingInvite, setIsAcceptingInvite] = useState<boolean>(false);
  const [isRefusingInvite, setIsRefusingInvite] = useState<boolean>(false);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [cancelSourceToken, setCancelSourceToken] = useState<CancelTokenSource>();
  const [invite, setInvite] = useState<Invite | undefined>();
  const [recipientIsCurrentUser, setRecipientIsCurrentUser] = useState<boolean>(false);
  const [relationshipType, setRelationshipType] = useState<RelationshipType | undefined>();
  const [relationAlreadyExists, setRelationAlreadyExists] = useState<boolean>(false);

  // Translations
  const {t} = useTranslation();

  // Get invite on component mount
  useEffect(() => {
    // Fetch invite
    const fetchInvite = async () => {
      // Start loading
      setIsInviteLoading(true);

      // Cancel token to cancel requests
      const cancelToken: CancelTokenStatic = axios.CancelToken;
      const source: CancelTokenSource = cancelToken.source();

      // Set the new cancel token
      setCancelSourceToken(source);

      if (props.identificationType) {
        try {
          // Response
          let response: BackendInvite<RelationshipInviteData & AffiliationInviteData> | undefined;

          // Get the invite
          if (props.identificationType === InviteIdentificationType.VoucherCode) {
            // Send request
            response = await InvitesRepository.instance.getInviteByVoucher(
              props.identificationValue?.trim(),
              source.token
            );
          } else if (props.identificationType === InviteIdentificationType.InviteId) {
            // Send request
            response = await InvitesRepository.instance.getInviteById(
              props.identificationValue?.trim(),
              source.token
            );
          }

          if (response) {
            if (
              response.type === InviteType.Relationship ||
              response.type === InviteType.Affiliation
            ) {
              // Set the invite
              setInvite({
                inviteId: response.id || '',
                type: response.type,
                inviteVoucherCode:
                  props.identificationType === InviteIdentificationType.VoucherCode
                    ? props.identificationValue?.trim()
                    : undefined,
                inviteState: response.state,
                inviteStatus: response.status,
                destinationUserId: response.data?.destinationId,
                destinationUserName: response.data?.destinationName,
                destinationUserPhoneNumber: response.data?.destinationPhoneNumber,
                destinationUserEmail: response.data?.destinationEmail,
                destinationUserRole: response.data?.destinationRole,
                createDate: response.createdAt,
                // ----- Relationship invite -----
                targetUserId: response.data?.targetUserId,
                targetUserPicture: response.data?.targetUser?.picture,
                targetUserName: response.data?.targetUser?.name,
                targetUserAffiliation:
                  response.data?.targetUser?.affiliations &&
                  Array.isArray(response.data?.targetUser?.affiliations)
                    ? response.data.targetUser.affiliations[0].entity.name
                    : undefined,
                // ----- Affiliation invite -----
                targetEntityId: response.data?.targetEntityId,
                targetEntityPicture: response.data?.targetEntity?.picture,
                targetEntityName: response.data?.targetEntity?.name,
                targetEntityAffiliationType: response.data?.type,
              });

              // Check if the invite was sent to the current user or not
              setRecipientIsCurrentUser(
                authContext.state.session?.id?.toString() ===
                  response.data?.destinationId?.toString() ||
                  authContext.state.session?.currentUser.email === response.data?.destinationEmail
              );

              // Check if the invite was already accepted or not
              let alreadyAccepted: boolean = false;
              if (response.type === InviteType.Relationship) {
                // Check if the relationship exists
                alreadyAccepted = authContext.state.session?.relationships.find(
                  (obj) => obj.withUser.id.toString() === response?.data?.targetUserId?.toString()
                )
                  ? true
                  : false;
              } else if (response.type === InviteType.Affiliation) {
                // Check if the affiliation exists
                alreadyAccepted = authContext.state.session?.affiliations.find(
                  (obj) => obj.entity.id.toString() === response?.data?.targetEntityId?.toString()
                )
                  ? true
                  : false;
              }
              setRelationAlreadyExists(alreadyAccepted);

              // Warning when the invite is done
              if (alreadyAccepted !== true && response.status === ProcessStatus.Done) {
                // Get the right warning
                let warning: string = '';
                switch (response.state) {
                  case InviteState.Accepted:
                    warning = t(translations.invite_modal.invite_done_accepted_description);
                    break;
                  case InviteState.Cancelled:
                    warning = t(translations.invite_modal.invite_done_cancelled_description);
                    break;
                  case InviteState.Refused:
                    warning = t(translations.invite_modal.invite_done_refused_description);
                    break;
                  case InviteState.Expired:
                    warning = t(translations.invite_modal.invite_done_expired_description);
                    break;
                  default:
                    warning = t(translations.invite_modal.invite_done_unknown_description);
                    break;
                }

                // Set it
                setInviteDoneWarning(warning);
              }

              // Reset state
              setIsAcceptingInvite(false);
              setIsRefusingInvite(false);
              setRelationshipType(undefined);
              setCanSubmit(response.type === InviteType.Affiliation ? true : false);

              // Stop loading
              setIsInviteLoading(false);
            } else {
              // Error
              throw new Error('Invalid invite type.');
            }
          } else {
            // Error
            throw new Error('Empty invite.');
          }
        } catch (error: any) {
          // Log
          console.debug('🔴 Could not open the invitation.', error);

          // Let's see if it's not a problem with the invitation
          // 403 - Expired or already used
          if (error.toString().search('403') !== -1) {
            // Error
            snackBarContext.setOfficeToastNotification({
              message: t(translations.invite_modal.errors.fetch_invite_invalid_error_message),
              severity: 'error',
            });
          } else if (error.message === CancelRequestError.CancelToken) {
            // The user closed the modal while it was loading
            // We don't show an error because it was their choice
            return;
          } else {
            // Unable to open the invitation
            snackBarContext.setOfficeToastNotification({
              message: t(translations.invite_modal.errors.fetch_invite_error_message),
              severity: 'error',
            });
          }

          // Close invite
          props.handleClose();
        }
      } else {
        // Log
        console.debug(
          '🔴 Could not open relationship invite because the identification type was not defined.'
        );

        // Unable to open the invite.
        snackBarContext.setOfficeToastNotification({
          message: t(translations.invite_modal.errors.fetch_invite_error_message),
          severity: 'error',
        });

        // Close invite
        props.handleClose();
      }
    };

    // Fetch invite
    fetchInvite();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.identificationType, props.identificationValue]);

  // Validation
  useEffect(() => {
    if (invite?.type === InviteType.Relationship) {
      if (relationshipType) {
        setCanSubmit(true);
      } else {
        setCanSubmit(false);
      }
    } else if (invite?.type === InviteType.Affiliation) {
      setCanSubmit(true);
    } else {
      setCanSubmit(false);
    }
  }, [invite, relationshipType]);

  // Cancel requests
  const handleCancelRequest = () => {
    // Cancel any request currently occurring
    if (isInviteLoading) {
      // Do we actually have a cancelToken?
      if (cancelSourceToken) {
        // Cancel it
        cancelSourceToken.cancel(CancelRequestError.CancelToken);
      }
    }
  };

  // Handle close
  const handleCloseModal = () => {
    // Cancel the request
    handleCancelRequest();

    // Close the modal
    props.handleClose();
  };

  // Handle relationship type changes
  const handleChangeRelationshipType = (event: ChangeEvent<HTMLInputElement>) => {
    // Change it
    setRelationshipType(event.target.value as RelationshipType);
  };

  // Handle refuse invite
  const handleRefuseInvite = async () => {
    try {
      // Refusing
      setIsRefusingInvite(true);

      // Request Body
      const requestBody: PatchResolveInvite =
        props.identificationType === InviteIdentificationType.VoucherCode
          ? {
              voucherCode: props.identificationValue?.trim(),
            }
          : {};

      // Refuse the invite
      await InvitesRepository.instance.resolveInvite(invite?.inviteId || '', false, requestBody);

      // Show success
      snackBarContext.setOfficeToastNotification({
        message:
          invite?.type === InviteType.Relationship
            ? t(translations.invite_modal.success.refuse_relationship_invite_success_message, {
                name: invite?.targetUserName,
              })
            : invite?.type === InviteType.Affiliation
              ? t(translations.invite_modal.success.refuse_affiliation_invite_success_message, {
                  name: invite?.targetEntityName,
                })
              : '',
        severity: 'success',
      });

      // Close modal
      props.handleClose();
    } catch (error) {
      // Log
      console.debug('🔴 Could not refuse the invite.', error);

      // Error
      snackBarContext.setOfficeToastNotification({
        message: t(translations.invite_modal.errors.refuse_invite_error_message),
        severity: 'error',
      });

      // Close modal
      props.handleClose();
    }

    // Stop refusing
    setIsRefusingInvite(false);
  };

  // Handle accept invite
  const handleAcceptInvite = async () => {
    try {
      // Accepting
      setIsAcceptingInvite(true);

      // Request Body
      const requestBody: PatchResolveInvite =
        props.identificationType === InviteIdentificationType.VoucherCode
          ? {
              relationshipType:
                invite?.type === InviteType.Relationship ? relationshipType : undefined,
              voucherCode: props.identificationValue?.trim(),
            }
          : {
              relationshipType:
                invite?.type === InviteType.Relationship ? relationshipType : undefined,
            };

      // Accept the invite
      await InvitesRepository.instance.resolveInvite(invite?.inviteId || '', true, requestBody);

      // Refresh session
      try {
        await authContext.refreshSessionData(
          authContext.state.session?.selectedRelationship?.id,
          authContext.state.session?.selectedAffiliation?.id
        );
      } catch (error) {
        // Log error but let's continue normally.
        // The data was saved on the backend so all is good.
        console.debug('🔴 Could not refresh session.', error);
      }

      // Show success
      snackBarContext.setOfficeToastNotification({
        message:
          invite?.type === InviteType.Relationship
            ? t(translations.invite_modal.success.accept_relationship_invite_success_message, {
                name: invite?.targetUserName,
              })
            : invite?.type === InviteType.Affiliation
              ? t(translations.invite_modal.success.accept_affiliation_invite_success_message, {
                  name: invite?.targetEntityName,
                })
              : '',
        severity: 'success',
      });

      // Close modal
      props.handleClose();
    } catch (error) {
      // Log
      console.debug('🔴 Could not refuse the invite.', error);

      // Error
      snackBarContext.setOfficeToastNotification({
        message: t(translations.invite_modal.errors.accept_invite_error_message),
        severity: 'error',
      });

      // Close modal
      props.handleClose();
    }

    // Stop accepting
    setIsAcceptingInvite(false);
  };

  return (
    <>
      {isInviteLoading ? (
        <Dialog scroll="paper" onClose={handleCloseModal} open={props.opened}>
          <DialogContent className="invite-modal-loading-dialog-container">
            <CircularProgress color="primary" size={20} />
            <Typography variant="body1" className="invite-modal-loading-dialog-upper-text">
              {t(translations.invite_modal.loading_invite_description_1)}
            </Typography>
            <Typography variant="body1" className="invite-modal-loading-dialog-lower-text">
              {t(translations.invite_modal.loading_invite_description_2)}
            </Typography>
          </DialogContent>
        </Dialog>
      ) : (
        <NotificationModal
          handleClose={handleCloseModal}
          open={props.opened}
          title={
            invite?.type === InviteType.Affiliation || invite?.type === InviteType.Relationship
              ? t(translations.invite_modal.caregiver_invite_title)
              : ''
          }
          avatarType={invite?.type === InviteType.Affiliation ? 'entity' : 'user'}
          avatarPicture={
            invite?.type === InviteType.Affiliation
              ? invite?.targetEntityPicture
              : invite?.type === InviteType.Relationship
                ? invite?.targetUserPicture
                : undefined
          }
          avatarName={
            invite?.type === InviteType.Affiliation
              ? invite?.targetEntityName
              : invite?.type === InviteType.Relationship
                ? invite?.targetUserName
                : ''
          }
          time={invite?.createDate}
        >
          <>
            {/* ---- User affiliation ---- */}
            {invite?.type === InviteType.Relationship && (
              <Typography variant="body1" className="invite-modal-description-text">
                {invite?.targetUserAffiliation}
              </Typography>
            )}
            {/* ---- Affiliation type ---- */}
            {invite?.type === InviteType.Affiliation && (
              <Typography variant="body1" className="invite-modal-description-text">
                {t(translations.invite_modal.affiliation_type_description, {
                  type: invite?.targetEntityAffiliationType
                    ? t(translations.affiliation_types[invite?.targetEntityAffiliationType])
                    : '',
                  name: invite.targetEntityName,
                })}
              </Typography>
            )}
            {/* ---- Relationship type input ---- */}
            {/* Invite not already accepted/refused */}
            {invite?.type === InviteType.Relationship &&
              relationAlreadyExists !== true &&
              invite?.inviteStatus !== ProcessStatus.Done && (
                <Box className="invite-modal-relationship-type-container">
                  <Typography variant="body1" className="invite-modal-relationship-type-label">
                    {t(translations.invite_modal.relationship_type_input, {
                      name: invite?.targetUserName,
                    })}
                  </Typography>
                  <FormControl
                    component="fieldset"
                    className="invite-modal-relationship-type-radio-group"
                  >
                    <RadioGroup onChange={handleChangeRelationshipType}>
                      {relationshipTypes.map((obj) => {
                        return (
                          <FormControlLabel
                            key={`relationship-invite-radio-group-option-${obj.type}`}
                            value={obj.type}
                            control={<Radio />}
                            label={t(obj.translation)}
                          />
                        );
                      })}
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            {/* ---- Warning ---- */}
            {/* Invite not already accepted/refused */}
            {relationAlreadyExists !== true &&
              invite?.inviteStatus !== ProcessStatus.Done &&
              recipientIsCurrentUser !== true && (
                <Box className="invite-modal-warning-container">
                  <WarningIcon fontSize="large" color="neutral" />
                  <Typography variant="body1" className="invite-modal-warning-text">
                    {t(translations.invite_modal.invite_recipient_not_current_user_description)}
                  </Typography>
                </Box>
              )}
            {/* ---- Warning ---- */}
            {/* The relationship/affiliation already exists */}
            {relationAlreadyExists === true && (
              <Box className="invite-modal-warning-container">
                <HowToRegIcon fontSize="large" color="neutral" />
                <Typography variant="body1" className="invite-modal-warning-text">
                  {invite?.type === InviteType.Affiliation
                    ? t(translations.invite_modal.affiliation_already_exists_description, {
                        name: invite.targetEntityName,
                      })
                    : invite?.type === InviteType.Relationship
                      ? t(translations.invite_modal.relationship_already_exists_description, {
                          name: invite.targetUserName,
                        })
                      : ''}
                </Typography>
              </Box>
            )}
            {/* ---- Warning ---- */}
            {/* Invite already used (accepted, refused, or other) and relationship/affiliation doesn't already exist */}
            {relationAlreadyExists !== true && invite?.inviteStatus === ProcessStatus.Done && (
              <Box className="invite-modal-warning-container">
                <MarkEmailReadIcon fontSize="large" color="neutral" />
                <Typography variant="body1" className="invite-modal-warning-text">
                  {inviteDoneWarning}
                </Typography>
              </Box>
            )}
            {/* ---- Buttons ---- */}
            {relationAlreadyExists !== true && invite?.inviteStatus !== ProcessStatus.Done && (
              <Box className="invite-modal-buttons-container">
                <LoadingButton
                  disabled={isRefusingInvite || isAcceptingInvite || !canSubmit}
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  loading={isAcceptingInvite}
                  loadingPosition="end"
                  endIcon={<CheckCircleOutlineIcon />}
                  onClick={handleAcceptInvite}
                >
                  {t(translations.invite_modal.accept_button)}
                </LoadingButton>
                <Box className="invite-modal-lower-button">
                  <LoadingButton
                    disabled={isRefusingInvite || isAcceptingInvite}
                    type="submit"
                    fullWidth
                    variant="text"
                    size="large"
                    loading={isRefusingInvite}
                    loadingPosition="end"
                    endIcon={<CancelIcon />}
                    onClick={handleRefuseInvite}
                  >
                    {t(translations.invite_modal.decline_button)}
                  </LoadingButton>
                </Box>
              </Box>
            )}
          </>
        </NotificationModal>
      )}
    </>
  );
}
